/**
 * StudentAnalyticsPage — score tracking + insights dashboard.
 *
 * Pulls /api/student/analytics/dashboard + /timeseries + /per-subject
 * and renders a single-page snapshot:
 *   • Summary tiles (streak, avg score, attempts, today active)
 *   • 14-day activity bars
 *   • 30-day score trend sparkline
 *   • Strengths + Weaknesses (top/bottom topics)
 *   • Per-subject breakdown table
 *
 * No chart libs — pure SVG keeps the bundle thin and matches the
 * minimalist student theme tokens (--st-bg / --st-ink / etc.).
 */
import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AccentRule, TINT_PRIMARY, TINT_ACCENT } from './studentUi';
import { TrendingUp, Flame, Trophy, AlertTriangle, Target, Activity, Clock, ChevronRight } from 'lucide-react';
import api from './studentApi';
import { friendlyError } from '../../lib/errors';
import { AnalyticsSkeleton } from './StudentSkeletons';

// Same threshold the backend splits strengths / focus zones on
// (student_analytics_routes.STRENGTH_MIN_PCT).
const STRENGTH_MIN_PCT = 70;

const fmtAxisDate = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// Measures the pixel width of a container so charts can fill the card
// instead of hugging its left edge at a fixed pixel width.
function useElementWidth(fallback = 480) {
  const ref = useRef(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth || fallback);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fallback]);
  return [ref, width];
}

function ChartTooltip({ left, children }) {
  return (
    <div
      className="absolute -translate-x-1/2 -translate-y-full pointer-events-none rounded-md px-2 py-1 text-xs font-semibold whitespace-nowrap shadow-md z-10"
      style={{ left, top: -6, background: 'var(--st-ink, #0f172a)', color: 'var(--st-card, #fff)' }}
    >
      {children}
    </div>
  );
}

function Spark({ data, width = 240, height = 60, stroke = 'var(--st-primary)' }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const vals = data.map(d => (d.avg_pct == null ? null : d.avg_pct));
  const real = vals.filter(v => v != null);
  if (real.length === 0) {
    return <div className="text-xs opacity-60">No quiz activity yet — your trend will appear here.</div>;
  }
  const min = Math.min(...real);
  const max = Math.max(...real);
  const range = Math.max(1, max - min);
  const plotWidth = width - 28;
  const xStep = plotWidth / Math.max(1, data.length - 1);
  const points = vals.map((v, i) => {
    const x = i * xStep;
    if (v == null) return null;
    const y = height - ((v - min) / range) * (height - 6) - 3;
    return [x, y];
  });
  const path = points.reduce((acc, p, i) => {
    if (!p) return acc;
    return acc + (acc ? ' L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1);
  }, '');

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const idx = Math.round((clientX - rect.left) / xStep);
    setHoverIdx(Math.max(0, Math.min(data.length - 1, idx)));
  };
  const clearHover = () => setHoverIdx(null);
  const hoverScore = hoverIdx != null ? vals[hoverIdx] : null;

  return (
    <div>
      <div className="flex items-center justify-between text-xs opacity-50 font-semibold mb-1.5">
        <span>Score %</span>
        <span>{fmtAxisDate(data[0].date)} – {fmtAxisDate(data[data.length - 1].date)}</span>
      </div>
      <div className="flex gap-1.5">
        <div className="flex flex-col justify-between text-xs opacity-50 shrink-0" style={{ height }}>
          <span>{Math.round(max)}</span>
          <span>{Math.round(min)}</span>
        </div>
        <div className="relative" style={{ width: plotWidth, height }}>
          <svg width={plotWidth} height={height} role="img" aria-label="30-day score trend, percent by date"
            onMouseMove={handleMove} onMouseLeave={clearHover}
            onTouchStart={handleMove} onTouchMove={handleMove} onTouchEnd={clearHover}
            style={{ touchAction: 'none', cursor: 'crosshair' }}>
            <path d={path} style={{ stroke }} strokeWidth="2" fill="none" strokeLinejoin="round" strokeLinecap="round" />
            {hoverIdx != null && (
              <line x1={hoverIdx * xStep} x2={hoverIdx * xStep} y1={0} y2={height} style={{ stroke }} strokeOpacity="0.25" strokeWidth="1" />
            )}
            {points.map((p, i) => p && (
              <circle key={i} cx={p[0]} cy={p[1]} r={hoverIdx === i ? 3.5 : 2} style={{ fill: stroke }} />
            ))}
          </svg>
          {hoverIdx != null && (
            <ChartTooltip left={Math.min(Math.max(hoverIdx * xStep, 28), plotWidth - 28)}>
              {fmtAxisDate(data[hoverIdx].date)} · {hoverScore == null ? 'No quiz' : `${hoverScore}%`}
            </ChartTooltip>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityBars({ data, width = 280, height = 60 }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const max = Math.max(1, ...data.map(d => d.count));
  const plotWidth = width - 28;
  const bw = plotWidth / Math.max(1, data.length);

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const idx = Math.floor((clientX - rect.left) / bw);
    setHoverIdx(Math.max(0, Math.min(data.length - 1, idx)));
  };
  const clearHover = () => setHoverIdx(null);

  return (
    <div>
      <div className="flex items-center justify-between text-xs opacity-50 font-semibold mb-1.5">
        <span>Activity count</span>
        <span>{data.length ? <>{fmtAxisDate(data[0].date)} – {fmtAxisDate(data[data.length - 1].date)}</> : null}</span>
      </div>
      <div className="flex gap-1.5">
        <div className="flex flex-col justify-between text-xs opacity-50 shrink-0" style={{ height }}>
          <span>{max}</span>
          <span>0</span>
        </div>
        <div className="relative" style={{ width: plotWidth, height }}>
          <svg width={plotWidth} height={height} role="img" aria-label="14-day activity count by date"
            onMouseMove={handleMove} onMouseLeave={clearHover}
            onTouchStart={handleMove} onTouchMove={handleMove} onTouchEnd={clearHover}
            style={{ touchAction: 'none', cursor: 'crosshair' }}>
          {data.map((d, i) => {
            const h = (d.count / max) * (height - 6);
            return (
              <rect key={d.date}
                x={i * bw + 1}
                y={height - h}
                width={bw - 2}
                height={Math.max(2, h)}
                rx={2}
                opacity={hoverIdx === null || hoverIdx === i ? 1 : 0.55}
                style={{ fill: d.count === 0 ? 'color-mix(in srgb, var(--st-ink) 10%, transparent)' : 'var(--st-primary)' }} />
            );
          })}
          </svg>
          {hoverIdx != null && (
            <ChartTooltip left={Math.min(Math.max(hoverIdx * bw + bw / 2, 28), plotWidth - 28)}>
              {fmtAxisDate(data[hoverIdx].date)} · {data[hoverIdx].count} {data[hoverIdx].count === 1 ? 'activity' : 'activities'}
            </ChartTooltip>
          )}
        </div>
      </div>
    </div>
  );
}

function MasteryBar({ pct, accent = 'var(--st-primary)' }) {
  return (
    <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: 'color-mix(in srgb, var(--st-ink) 8%, transparent)' }}>
      <div className="h-2 rounded-full" style={{ width: `${Math.min(100, Math.max(0, pct))}%`, background: accent }} />
    </div>
  );
}

function Tile({ icon: Icon, label, value, sub, color = '#10B981', testid }) {
  return (
    <div data-testid={testid}
      className="rounded-2xl p-4 border flex items-center gap-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
      style={{ background: 'var(--st-card, #fff)', borderColor: 'var(--st-border, #e5e7eb)' }}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `color-mix(in srgb, ${color} 16%, var(--st-card))`, color: `color-mix(in srgb, ${color} 60%, var(--st-ink))` }}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="text-sm" style={{ color: 'var(--st-mute)' }}>{label}</div>
        <div className="text-2xl font-semibold leading-tight tabular-nums" style={{ color: 'var(--st-ink, #0f172a)', fontFamily: 'var(--st-font-heading)' }}>{value}</div>
        {sub && <div className="text-xs opacity-60 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

export default function StudentAnalyticsPage() {
  const [trendRef, trendWidth] = useElementWidth();
  const [activityRef, activityWidth] = useElementWidth();

  const { data: page, isPending: loading, error } = useQuery({
    queryKey: ['student', 'analytics'],
    queryFn: async () => {
      const [dashboard, t, ps] = await Promise.all([
        // The dashboard is the page — if it fails, say so instead of "No analytics yet".
        api.get('/student/analytics/dashboard'),
        api.get('/student/analytics/timeseries?days=30').catch(() => null),
        api.get('/student/analytics/per-subject').catch(() => null),
      ]);
      return { dashboard, series: (t && t.series) || [], perSubject: (ps && ps.rows) || [] };
    },
    meta: { silent: true },
  });
  const data = page?.dashboard ?? null;
  const series = page?.series ?? [];
  const perSubject = page?.perSubject ?? [];
  const err = error ? friendlyError(error, 'Could not load analytics.') : null;

  if (loading) return <AnalyticsSkeleton />;
  if (err)    return <div className="p-8 text-sm text-red-500">{err}</div>;
  if (!data)  return <div className="p-8 text-sm opacity-70">No analytics yet.</div>;

  const s = data.summary || {};

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 py-6 space-y-6" data-testid="student-analytics-page">
      {/* Header */}
      <header>
        <div className="text-xs font-semibold opacity-60">Insights</div>
        <h1 className="text-3xl font-semibold leading-tight" style={{ color: 'var(--st-ink)' }}>Your learning, by the numbers</h1>
<AccentRule className="mt-2" />
        <p className="text-sm opacity-70 mt-1">
          Score-tracking · streak · mastery breakdown — purely educational signals, no advertising.
        </p>
      </header>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-summary-tiles">
        <Tile icon={Flame}      label="Streak"       value={`${s.streak_current || 0}d`}
              sub={`Longest ${s.streak_longest || 0}d`} color="#F59E0B" testid="tile-streak" />
        <Tile icon={Target}     label="Avg score"     value={s.avg_pct == null ? '—' : `${s.avg_pct}%`}
              sub={s.questions_total
                ? `${s.questions_total} question${s.questions_total === 1 ? '' : 's'} · ${s.attempts_total} quiz${s.attempts_total === 1 ? '' : 'zes'}`
                : 'No quizzes yet'}
              color="#10B981" testid="tile-avg" />
        <Tile icon={Activity}   label="Quizzes"       value={s.events_total || 0}
              sub="Last 90 days" color="#0EA5E9" testid="tile-events" />
        <Tile icon={Clock}      label="Today"        value={s.today_active ? (s.today_summary || 'Active') : 'Idle'}
              sub={s.today_active ? 'Keep the streak alive' : 'Try a 5-min quiz'} color={s.today_active ? '#10B981' : '#94A3B8'} testid="tile-today" />
      </div>

      {/* Trend + activity */}
      <div className="grid md:grid-cols-2 gap-3">
        <div data-testid="trend-card" className="rounded-2xl p-5 border" style={{ background: 'var(--st-card, #fff)', borderColor: 'var(--st-border, #e5e7eb)' }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={TINT_PRIMARY}><TrendingUp className="w-4 h-4" /></span>
            <h3 className="text-base font-semibold" style={{ color: 'var(--st-ink)' }}>30-day score trend</h3>
          </div>
          <div ref={trendRef}>
            <Spark data={series} width={trendWidth} height={70} />
          </div>
        </div>
        <div data-testid="activity-card" className="rounded-2xl p-5 border" style={{ background: 'var(--st-card, #fff)', borderColor: 'var(--st-border, #e5e7eb)' }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={TINT_PRIMARY}><Activity className="w-4 h-4" /></span>
            <h3 className="text-base font-semibold" style={{ color: 'var(--st-ink)' }}>14-day activity</h3>
          </div>
          <div ref={activityRef}>
            <ActivityBars data={data.activity_14d || []} width={activityWidth} height={70} />
          </div>
        </div>
      </div>

      {/* Strengths + Weaknesses */}
      <div className="grid md:grid-cols-2 gap-3">
        <div data-testid="strengths-card" className="rounded-2xl p-5 border" style={{ background: 'var(--st-card, #fff)', borderColor: 'var(--st-border, #e5e7eb)' }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={TINT_PRIMARY}><Trophy className="w-4 h-4" /></span>
            <h3 className="text-base font-semibold" style={{ color: 'var(--st-ink)' }}>Strengths</h3>
            <div className="text-xs opacity-50 ml-auto">avg {STRENGTH_MIN_PCT}%+</div>
          </div>
          <ul className="space-y-3">
            {(data.strengths || []).map(t => (
              <li key={t.subject} className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{t.subject}</div>
                  <div className="text-xs opacity-60">
                    {t.topic_count} topic{t.topic_count === 1 ? '' : 's'} · {t.n} attempt{t.n === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="w-32"><MasteryBar pct={t.avg_pct} accent="#10B981" /></div>
                <div className="text-xs font-semibold w-10 text-right">{Math.round(t.avg_pct)}%</div>
              </li>
            ))}
            {(!data.strengths || data.strengths.length === 0) && (
              <li className="text-xs opacity-60">
                {s.events_total
                  ? `No subject averages ${STRENGTH_MIN_PCT}% yet — practise your focus zones to get there.`
                  : 'Run a few quizzes to unlock your strength map.'}
              </li>
            )}
          </ul>
        </div>
        <div data-testid="weaknesses-card" className="rounded-2xl p-5 border" style={{ background: 'var(--st-card, #fff)', borderColor: 'var(--st-border, #e5e7eb)' }}>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={TINT_ACCENT}><AlertTriangle className="w-4 h-4" /></span>
            <h3 className="text-base font-semibold" style={{ color: 'var(--st-ink)' }}>Focus zones</h3>
            <div className="text-xs opacity-50 ml-auto">avg below {STRENGTH_MIN_PCT}%</div>
          </div>
          <ul className="space-y-3">
            {(data.weaknesses || []).map(t => (
              <li key={t.subject} className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{t.subject}</div>
                  <div className="text-xs opacity-60">
                    {t.topic_count} topic{t.topic_count === 1 ? '' : 's'} · {t.n} attempt{t.n === 1 ? '' : 's'}
                  </div>
                </div>
                <div className="w-32"><MasteryBar pct={t.avg_pct} accent="#F97316" /></div>
                <div className="text-xs font-semibold w-10 text-right">{Math.round(t.avg_pct)}%</div>
              </li>
            ))}
            {(!data.weaknesses || data.weaknesses.length === 0) && (
              <li className="text-xs opacity-60">
                {!s.events_total
                  ? 'Not enough data yet — take a quiz to see your focus zones.'
                  : <>You&apos;re on top of every topic</>}
              </li>
            )}
          </ul>
        </div>
      </div>

      {/* Per-subject */}
      <div data-testid="per-subject-card" className="rounded-2xl p-5 border" style={{ background: 'var(--st-card, #fff)', borderColor: 'var(--st-border, #e5e7eb)' }}>
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={TINT_PRIMARY}><ChevronRight className="w-4 h-4" /></span>
          <h3 className="text-base font-semibold" style={{ color: 'var(--st-ink)' }}>By subject</h3>
          <div className="text-xs opacity-50 ml-auto">Last 90 days</div>
        </div>
        {perSubject.length === 0 ? (
          <div className="text-xs opacity-60">Subject breakdown will appear once you take a few quizzes.</div>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'var(--st-border, #e5e7eb)' }}>
            {perSubject.map(r => (
              <li key={r.subject} className="py-2.5 grid grid-cols-12 gap-3 items-center">
                <div className="col-span-4 text-sm font-semibold">{r.subject}</div>
                <div className="col-span-2 text-xs opacity-70">{r.events} quiz{r.events === 1 ? '' : 'zes'}</div>
                <div className="col-span-2 text-xs opacity-70">{r.topic_count} topic{r.topic_count === 1 ? '' : 's'}</div>
                <div className="col-span-3"><MasteryBar pct={r.avg_pct} /></div>
                <div className="col-span-1 text-right text-xs font-semibold">{r.avg_pct}%</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
